import React, { useEffect, useState } from "react";
import FrontendLayout from "../FrontendLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { BOOKING } from "../../data/url";
import Alert from "../../utils/Alert";
import httpClient from "../../http/HttpRequest";
import QuoteProgress from "../front-components/QuoteProgress";

export default function Payment() {
  const location = useLocation();
  const navigate = useNavigate();
  const [distance, setDistance] = useState({});
  const [formData, setFormData] = useState({});
  const [totals, setTotals] = useState({});
  const [paymentMode] = useState("cash-on-delivery");

  useEffect(() => {
    if (location.state?.distanceDetails) {
      setDistance(location.state.distanceDetails);
    }
    if (location.state?.formData) {
      setFormData(location.state.formData);
    }
    if (location.state?.totals) {
      setTotals(location.state.totals);
    }
  }, [location.state]);

  /* Temporarily disabled along with the payment options below.
  const handlePaymentModeChange = (mode) => {
    if (mode === "upi" || mode === "vegaah") {
      Alert.info("Coming Soon");
      return;
    }

    setPaymentMode(mode);
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (paymentMode !== "cash-on-delivery") {
      Alert.info("Coming Soon");
      return;
    }

    let payload = {
      distance: distance,
      formData: formData,
      paymentMode: paymentMode,
    };
    const res = await httpClient.post(BOOKING, payload);
    if (res.requestStatus === "OK") {
      const result = res.result;
      if (result.status === 1) {
        const receipt = {
          bookingId: result.data?.booking_id,
          amount: result.data?.amount ?? totals.amount,
          tax: result.data?.tax ?? totals.tax,
          total: result.data?.netamount ?? totals.netamount,
          distance,
          formData,
          paymentMode,
          createdAt: new Date().toISOString(),
        };

        sessionStorage.setItem("lastBookingReceipt", JSON.stringify(receipt));
        Alert.success(result.message);
        navigate("/booking-receipt", {
          state: { receipt },
          replace: true,
        });
      } else {
        Alert.error(result.message);
      }
    }
  };

  return (
    <FrontendLayout>
      <main className="quote-flow-page">
        <div className="container quote-flow-container">
          <div className="quote-flow-heading wow fadeInUp" data-wow-delay="0.1s">
            <span className="quote-flow-eyebrow">
              <i className="fa fa-shield-alt"></i> Secure booking review
            </span>
            <h1>Review your quote</h1>
            <p>
              Confirm shipment details and place your booking.
            </p>
          </div>
          <QuoteProgress activeStep={3} />

          <div className="row g-4 align-items-start">
            <div className="col-12 col-xl-7">
              <section className="quote-review-card wow fadeIn" data-wow-delay="0.2s">
                <div className="quote-card-heading">
                  <span className="quote-surface-icon">
                    <i className="fa fa-clipboard-check"></i>
                  </span>
                  <div>
                    <span className="quote-surface-kicker">Booking details</span>
                    <h2>Your shipment</h2>
                  </div>
                </div>

                <div className="payment-route-card">
                  <div className="payment-route-point">
                    <span><i className="fa fa-map-marker-alt"></i></span>
                    <div>
                      <small>Pickup</small>
                      <strong>{distance.pickupLocation}</strong>
                    </div>
                  </div>
                  <div className="payment-route-arrow">
                    <i className="fa fa-long-arrow-alt-right"></i>
                  </div>
                  <div className="payment-route-point">
                    <span><i className="fa fa-flag-checkered"></i></span>
                    <div>
                      <small>Delivery</small>
                      <strong>{distance.dropLocation}</strong>
                    </div>
                  </div>
                </div>

                <div className="quote-detail-grid">
                  <div className="quote-detail-item">
                    <span><i className="fa fa-user"></i> Customer</span>
                    <strong>{formData.name}</strong>
                  </div>
                  <div className="quote-detail-item">
                    <span><i className="fa fa-phone-alt"></i> Mobile</span>
                    <strong>{formData.mobile}</strong>
                  </div>
                  <div className="quote-detail-item">
                    <span><i className="fa fa-truck"></i> Truck</span>
                    <strong>{formData.selectedTruck?.label || ""}</strong>
                  </div>
                  <div className="quote-detail-item">
                    <span><i className="fa fa-box"></i> Category</span>
                    <strong>{formData.selectedCategory?.label || ""}</strong>
                  </div>
                  <div className="quote-detail-item">
                    <span><i className="fa fa-weight-hanging"></i> Weight</span>
                    <strong>{formData.weight} {formData.weightType}</strong>
                  </div>
                  <div className="quote-detail-item">
                    <span><i className="fa fa-road"></i> Journey</span>
                    <strong>{distance.distance} · {distance.duration}</strong>
                  </div>
                </div>
              </section>
            </div>

            <div className="col-12 col-xl-5">
              <section className="quote-pricing-card wow fadeIn" data-wow-delay="0.25s">
                <div className="quote-pricing-header">
                  <div>
                    <span>Order summary</span>
                    <h2>Transparent pricing</h2>
                  </div>
                  <span className="quote-pricing-badge">INR</span>
                </div>

                <div className="quote-price-lines">
                  <div className="quote-price-line">
                    <span>Transport amount</span>
                    <strong>₹{totals.amount}</strong>
                  </div>
                  <div className="quote-price-line">
                    <span>Applicable tax</span>
                    <strong>₹{totals.tax}</strong>
                  </div>
                  <div className="quote-price-total">
                    <div>
                      <span>Total payable</span>
                      <small>Inclusive of applicable taxes</small>
                    </div>
                    <strong>₹{totals.netamount}</strong>
                  </div>
                </div>

                {/* Payment options temporarily hidden: Cash on Delivery, UPI and Vegaah.
                <fieldset className="payment-methods">
                  <legend>Select payment mode</legend>
                  <label
                    className={`payment-method ${
                      paymentMode === "cash-on-delivery" ? "is-selected" : ""
                    }`}
                    htmlFor="cashOnDelivery"
                  >
                    <input
                      type="radio"
                      name="paymentMode"
                      id="cashOnDelivery"
                      value="cash-on-delivery"
                      checked={paymentMode === "cash-on-delivery"}
                      onChange={() => handlePaymentModeChange("cash-on-delivery")}
                    />
                    <span className="payment-method-copy">
                      <strong>Cash on Delivery</strong>
                      <small>Pay after service confirmation</small>
                    </span>
                    <img
                      src="/assets/img/payment/cash-on-delivery.svg"
                      alt="Cash on Delivery"
                    />
                  </label>

                  <label
                    className={`payment-method ${paymentMode === "upi" ? "is-selected" : ""}`}
                    htmlFor="upiPayment"
                  >
                    <input
                      type="radio"
                      name="paymentMode"
                      id="upiPayment"
                      value="upi"
                      checked={paymentMode === "upi"}
                      onChange={() => handlePaymentModeChange("upi")}
                    />
                    <span className="payment-method-copy">
                      <strong>UPI</strong>
                      <small>Fast payment from any UPI app</small>
                    </span>
                    <img
                      src="/assets/img/payment/upi-payment.svg"
                      alt="UPI Payment"
                    />
                  </label>

                  <label
                    className={`payment-method ${paymentMode === "vegaah" ? "is-selected" : ""}`}
                    htmlFor="vegaahPayment"
                  >
                    <input
                      type="radio"
                      name="paymentMode"
                      id="vegaahPayment"
                      value="vegaah"
                      checked={paymentMode === "vegaah"}
                      onChange={() => handlePaymentModeChange("vegaah")}
                    />
                    <span className="payment-method-copy">
                      <strong>Vegaah Payment</strong>
                      <small>Secure online payment gateway</small>
                    </span>
                    <span className="payment-logo-dark">
                      <img
                        src="/assets/img/payment/vegaah-payment.png"
                        alt="Vegaah Payment Gateway"
                      />
                    </span>
                  </label>
                </fieldset>
                */}

                <aside className="payment-review-notice" aria-labelledby="payment-review-title">
                  <div className="payment-review-notice-header">
                    <span className="payment-review-notice-icon" aria-hidden="true">
                      <i className="fa fa-info"></i>
                    </span>
                    <div>
                      <span className="payment-review-notice-eyebrow">Before you confirm</span>
                      <h3 id="payment-review-title">Important information</h3>
                    </div>
                  </div>
                  <p>
                    We will review your order and, once approved, send a
                    confirmation and payment link to your registered mobile
                    number and email address.
                  </p>
                  <div className="payment-review-notice-delivery">
                    <i className="fa fa-link" aria-hidden="true"></i>
                    <span>Payment link after approval</span>
                    <span className="payment-review-notice-channels" aria-hidden="true">
                      <i className="fa fa-mobile-alt"></i>
                      <i className="fa fa-envelope"></i>
                    </span>
                  </div>
                  <strong className="payment-review-notice-footer">
                    <i className="fa fa-check-circle" aria-hidden="true"></i>
                    No payment is required at this stage.
                  </strong>
                </aside>

                <div className="quote-booking-actions">
                  <button
                    className="quote-secondary-action"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    <i className="fa fa-arrow-left"></i> Edit
                  </button>
                  <button
                    className="quote-primary-action quote-pay-action"
                    type="button"
                    onClick={handleSubmit}
                  >
                    Confirm booking <i className="fa fa-lock"></i>
                  </button>
                </div>

                <p className="quote-secure-note">
                  <i className="fa fa-shield-alt"></i> Your booking and payment
                  details are securely processed.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
    </FrontendLayout>
  );
}
